import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "../lib/scroll";

const lines = ["Betaalbaar", "wonen in", "Nederland."];

const HeroImage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <div ref={ref} className="relative">
      <motion.div
        initial={{ clipPath: "inset(100% 0 0 0)" }}
        animate={{ clipPath: "inset(0% 0 0 0)" }}
        transition={{ duration: 1.2, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <motion.img
          src="https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?auto=format&fit=crop&w=1200&q=80"
          alt="Minimalistische Nederlandse architectuur"
          style={{ y }}
          className="w-full h-[340px] md:h-[560px] object-cover scale-110"
        />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.4 }}
        className="absolute -bottom-6 -left-6 md:-left-10 bg-foreground text-background px-6 py-5"
      >
        <p className="font-display font-black text-3xl leading-none">500+</p>
        <p className="text-xs tracking-widest uppercase mt-1 opacity-70">
          Betaalbare woningen
        </p>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-[72px] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-28 pb-24 md:pb-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-end">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xs md:text-sm tracking-[0.3em] uppercase text-muted-foreground mb-8"
            >
              Makelaarsplatform — rentguru.nl
            </motion.p>

            <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95]">
              {lines.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{
                      duration: 1,
                      delay: 0.2 + i * 0.14,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="block"
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-10 max-w-md text-base md:text-lg text-muted-foreground leading-relaxed"
            >
              Rent Guru vindt betaalbare huurhuizen in heel Nederland. Geen
              verborgen kosten, geen eindeloze wachtlijsten — direct reageren op
              jouw nieuwe thuis.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <button
                data-testid="hero-cta-woningen"
                onClick={() => scrollToId("#woningen")}
                className="bg-foreground text-background px-8 py-4 text-sm font-medium tracking-wide hover:-translate-y-0.5 transition-transform duration-200"
              >
                Bekijk het aanbod
              </button>
              <button
                data-testid="hero-cta-over-ons"
                onClick={() => scrollToId("#over-ons")}
                className="border border-foreground px-8 py-4 text-sm font-medium tracking-wide hover:bg-foreground hover:text-background transition-colors duration-200"
              >
                Hoe het werkt
              </button>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <HeroImage />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-24 md:mt-32 grid grid-cols-3 gap-6 border-t border-border pt-8"
        >
          {[
            ["€0", "Courtage of inschrijfkosten"],
            ["12", "Steden en groeiend"],
            ["< 24u", "Reactie op je aanvraag"],
          ].map(([num, label]) => (
            <div key={label}>
              <p className="font-display font-black text-2xl md:text-4xl tracking-tight">
                {num}
              </p>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">
                {label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
